import { ref, watchEffect } from 'vue';
import { useFlag } from '@loadsmart/vue-feature-flags';

import { toPlainCompany } from '@/modules/company/companyDisplaySettings';

/**
 * Soft gate for Luna company date/time display prefs (SUR-271).
 *
 * When disabled (or not yet ready), company prefs are ignored: pattern helpers expose
 * `undefined` patterns so legacy chronon enum / warehouse-military paths stay intact.
 * When enabled, format helpers use carrier company dateDisplayFormat + timeDisplayFormat.
 *
 * {@link companyDisplayFormatEnabled} is the single source of truth. Vue setups sync
 * Unleash into it via {@link useCompanyDisplayFormatGate}; non-Vue code (e.g.
 * ExtendedAppointment) and Options API methods read the same ref.
 */
export const COMPANY_DISPLAY_FORMAT_FLAG = 'enable-luna-company-display-format';

/** Shared soft-gate state — read this (or {@link isCompanyDisplayFormatEnabled}) everywhere. */
export const companyDisplayFormatEnabled = ref(false);

/** Test / manual override. Prefer {@link useCompanyDisplayFormatGate} in Vue setups. */
export function setCompanyDisplayFormatEnabled(enabled) {
  companyDisplayFormatEnabled.value = Boolean(enabled);
}

export function isCompanyDisplayFormatEnabled() {
  return companyDisplayFormatEnabled.value;
}

/**
 * Syncs Unleash `enable-luna-company-display-format` into {@link companyDisplayFormatEnabled}.
 * Call once from App setup (for non-Vue consumers) and from useCompanySettings.
 */
export function useCompanyDisplayFormatGate() {
  const flag = useFlag(COMPANY_DISPLAY_FORMAT_FLAG);
  watchEffect(() => {
    companyDisplayFormatEnabled.value = Boolean(flag.value?.ready && flag.value?.enabled);
  });
  return companyDisplayFormatEnabled;
}

/**
 * Returns company for format/parse helpers when the soft gate is on; otherwise null
 * (callers must use legacy warehouse-military / chronon enums when null).
 * Always returns a plain (non-reactive) company so libs/core stays free of Vue Proxies.
 * @param {object|null|undefined} company
 */
export function companyForDisplay(company) {
  if (!companyDisplayFormatEnabled.value) {
    return null;
  }
  return toPlainCompany(company);
}
